// src/components/TrebleTownMapModal.jsx
// Reusable modal that renders the Treble Town winding-path map.
// - Locked levels → /pwp-paywall
// - Unlocked/complete levels → navigate normally
// - Used in PracticeMode (post-completion) and LevelTemplate (pill button)

import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getMaxUnlockedLevel } from "../services/unlockRules";
import { TrophyStorage } from "../services/trophyStorage";

/* ── Level metadata ───────────────────────────────────────────── */
const LEVELS = [
  { id: 1,  name: "Doron Attacks",      song: "Twinkle Twinkle",       emoji: "🐉" },
  { id: 2,  name: "Zeb's Birthday",     song: "Happy Birthday",         emoji: "🦓" },
  { id: 3,  name: "Penguin's Melt",     song: "Ode to Joy",             emoji: "🐧" },
  { id: 4,  name: "Treb Lake",          song: "Machli Jal Ki Rani",     emoji: "🐟" },
  { id: 5,  name: "Ren's Connect",      song: "Jingle Bells",           emoji: "🦌" },
  { id: 6,  name: "Spider Dance",       song: "Incy Wincy Spider",      emoji: "🕷️" },
  { id: 7,  name: "Bamboo Forest",      song: "London Bridge",          emoji: "🎋" },
  { id: 8,  name: "Peacock Parade",     song: "National Anthem",        emoji: "🦚" },
  { id: 9,  name: "Crocodile Test",     song: "Row Row Row Your Boat",  emoji: "🐊" },
  { id: 10, name: "Final Doron Battle", song: "Find the ancient melody...", emoji: "👹", isBoss: true },
];

/* ── Winding path node positions (360×940 viewBox) ───────────── */
const NODES = [
  { id: 1,  cx: 180, cy: 876 },
  { id: 2,  cx: 72,  cy: 766 },
  { id: 3,  cx: 288, cy: 660 },
  { id: 4,  cx: 72,  cy: 560 },
  { id: 5,  cx: 288, cy: 466 },
  { id: 6,  cx: 72,  cy: 378 },
  { id: 7,  cx: 288, cy: 296 },
  { id: 8,  cx: 72,  cy: 214 },
  { id: 9,  cx: 288, cy: 132 },
  { id: 10, cx: 180, cy: 52  },
];

const PATH_D = [
  "M 180 876",
  "C 180 836 72 806 72 766",
  "C 72 726 288 700 288 660",
  "C 288 618 72 598 72 560",
  "C 72 520 288 500 288 466",
  "C 288 424 72 408 72 378",
  "C 72 338 288 326 288 296",
  "C 288 254 72 244 72 214",
  "C 72 172 288 162 288 132",
  "C 288 90 180 78 180 52",
].join(" ");

const STARQUEST_KEY = "tt_starquest_completed_levels_v1";

function getStarLevels() {
  try {
    const arr = JSON.parse(localStorage.getItem(STARQUEST_KEY) || "[]");
    return new Set(Array.isArray(arr) ? arr.map(Number) : []);
  } catch { return new Set(); }
}

function getLevelPath(id) {
  return `/level-${id}`;
}

/* ── Main Modal ───────────────────────────────────────────────── */
export default function TrebleTownMapModal({ open, onClose }) {
  const navigate = useNavigate();
  const [lockedTip, setLockedTip] = useState(null);

  const { maxUnlocked, levelsCompleted, starLevels } = useMemo(() => {
    if (!open) return { maxUnlocked: 1, levelsCompleted: new Set(), starLevels: new Set() };
    const maxUnlocked = Math.max(1, Number(getMaxUnlockedLevel?.() || 1));
    const s = TrophyStorage.getState?.() || {};
    const doneMap = s?.roundInProgress?.levelsCompleted || {};
    const done = new Set(Object.keys(doneMap).filter(k => doneMap[k]).map(k => Number(k)));
    return { maxUnlocked, levelsCompleted: done, starLevels: getStarLevels() };
  }, [open]);

  if (!open) return null;

  function getStatus(id) {
    if (levelsCompleted.has(id)) return "complete";
    if (id === maxUnlocked) return "current";
    if (id < maxUnlocked) return "unlocked";
    return "locked";
  }

  function handleNodeClick(e, level) {
    e.stopPropagation();
    const status = getStatus(level.id);
    if (status === "locked") {
      setLockedTip(lockedTip === level.id ? null : level.id);
      return;
    }
    onClose();
    navigate(getLevelPath(level.id));
  }

  function handleLockedPaywall(e, level) {
    e.stopPropagation();
    onClose();
    navigate(`/pwp-paywall?next=${encodeURIComponent(getLevelPath(level.id))}`);
  }

  const doneCount   = levelsCompleted.size;
  const starCount   = starLevels.size;

  return (
    <div
      style={OVERLAY}
      onClick={() => { setLockedTip(null); onClose(); }}
    >
      <div style={SHEET} onClick={e => e.stopPropagation()}>

        {/* ── Header ── */}
        <div style={HEADER}>
          <div>
            <div style={HEADER_TITLE}>🗺️ Treble Town</div>
            <div style={HEADER_SUB}>{doneCount}/10 done · ⭐ {starCount}/8 stars</div>
          </div>
          <button style={CLOSE_BTN} onClick={onClose} aria-label="Close map">✕</button>
        </div>

        {/* ── Scrollable map ── */}
        <div style={MAP_SCROLL} onClick={() => setLockedTip(null)}>
          <svg
            viewBox="0 0 360 940"
            xmlns="http://www.w3.org/2000/svg"
            style={{ width: "100%", display: "block", overflow: "visible" }}
          >
            <defs>
              <linearGradient id="ttmm-sky" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"  stopColor="#a8e8f8"/>
                <stop offset="58%" stopColor="#cdf7d5"/>
                <stop offset="100%" stopColor="#8dd65a"/>
              </linearGradient>
              <radialGradient id="ttmm-boss" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#ff5252" stopOpacity=".4"/>
                <stop offset="100%" stopColor="#ff5252" stopOpacity="0"/>
              </radialGradient>
            </defs>

            <rect width={360} height={940} fill="url(#ttmm-sky)"/>
            <MapDecos/>

            {/* Path */}
            <path d={PATH_D} fill="none" stroke="rgba(0,0,0,.12)" strokeWidth={18} strokeLinecap="round" transform="translate(1,5)"/>
            <path d={PATH_D} fill="none" stroke="#d8c49a" strokeWidth={18} strokeLinecap="round"/>
            <path d={PATH_D} fill="none" stroke="#ede3c8" strokeWidth={11} strokeLinecap="round"/>
            <path d={PATH_D} fill="none" stroke="rgba(255,255,255,.6)" strokeWidth={5} strokeLinecap="round" strokeDasharray="10 14"/>

            {LEVELS.map((level, i) => {
              const node   = NODES[i];
              const status = getStatus(level.id);
              const hasStar = starLevels.has(level.id) || (status === "complete" && level.id >= 2 && level.id <= 9);
              return (
                <MapNode
                  key={level.id}
                  level={level}
                  node={node}
                  status={status}
                  hasStar={hasStar}
                  showTip={lockedTip === level.id}
                  onClick={(e) => handleNodeClick(e, level)}
                  onPaywall={(e) => handleLockedPaywall(e, level)}
                />
              );
            })}
          </svg>
        </div>

        {/* ── Footer legend ── */}
        <div style={LEGEND}>
          {[
            ["#52b788","Done"], ["#f4a629","Current"],
            ["#3b82f6","Unlocked"], ["#b0b0b0","Locked"], ["#8a0000","Boss"],
          ].map(([color, label]) => (
            <span key={label} style={LEG_ITEM}>
              <span style={{ ...LEG_DOT, background: color }}/>
              {label}
            </span>
          ))}
        </div>
      </div>

      <style>{MODAL_CSS}</style>
    </div>
  );
}

/* ── Map Node ─────────────────────────────────────────────────── */
function MapNode({ level, node, status, hasStar, showTip, onClick, onPaywall }) {
  const { cx, cy } = node;
  const isBoss   = !!level.isBoss;
  const isLocked = status === "locked";
  const r        = isBoss ? 42 : 32;

  const ring = {
    complete: "#52b788", current: "#f4a629",
    unlocked: "#3b82f6", locked:  "#b0b0b0",
  }[status] || "#b0b0b0";

  const bg = {
    complete: "#f0faf4", current: "#fffbec",
    unlocked: "#eff6ff", locked:  "#f5f5f5",
  }[status] || "#f5f5f5";

  const textFill = {
    complete: "#1b5e38", current: "#7c5600",
    unlocked: "#1e3a8a", locked:  "#888",
  }[status] || "#888";

  const bossRing   = isBoss && !isLocked ? "#c62828" : ring;
  const bossBg     = isBoss && !isLocked ? "#fff4f4" : bg;
  const bossText   = isBoss && !isLocked ? "#7f0000" : textFill;

  const finalRing = isBoss ? bossRing : ring;
  const finalBg   = isBoss ? bossBg   : bg;
  const finalText = isBoss ? bossText : textFill;

  return (
    <g transform={`translate(${cx},${cy})`} style={{ cursor: isLocked ? "pointer" : "pointer" }} onClick={onClick}>
      {/* Boss halo */}
      {isBoss && !isLocked && (
        <circle r={r + 22} fill="url(#ttmm-boss)" className="ttmm-boss-pulse"/>
      )}
      {/* Current pulse ring */}
      {status === "current" && (
        <circle r={r + 10} fill="none" stroke={finalRing} strokeWidth={3} opacity={0.65} className="ttmm-cur-pulse"/>
      )}
      {/* Shadow */}
      <circle r={r + 3} fill="rgba(0,0,0,0.13)" transform="translate(0,5)"/>
      {/* Ring */}
      <circle r={r + 3} fill={finalRing}/>
      {/* Face */}
      <circle r={r} fill={finalBg}/>
      {status === "complete" && <circle r={r} fill={finalRing} opacity={0.18}/>}

      {/* Icon */}
      {isLocked ? (
        <>
          <rect x={-7} y={-4} width={14} height={11} rx={2} fill="#888"/>
          <path d="M-4.5-4 A4.5 4.5 0 0 1 4.5-4" fill="none" stroke="#888" strokeWidth={2.8} strokeLinecap="round"/>
        </>
      ) : (
        <text textAnchor="middle" dominantBaseline="middle"
          y={isBoss ? -7 : -5} fontSize={isBoss ? 24 : 18} style={{ userSelect: "none" }}>
          {level.emoji}
        </text>
      )}

      {/* Level label */}
      {!isLocked && (
        <text textAnchor="middle" dominantBaseline="middle"
          y={isBoss ? 15 : 13} fontSize={isBoss ? 11 : 10}
          fontWeight="900" fontFamily="'Fredoka One','Nunito',sans-serif"
          fill={finalText} style={{ userSelect: "none" }}>
          {isBoss ? "BOSS" : `Lv ${level.id}`}
        </text>
      )}

      {/* Star */}
      {hasStar && (
        <text textAnchor="middle" y={r + 17} fontSize={13} style={{ userSelect: "none" }}>⭐</text>
      )}

      {/* Level name */}
      {!isLocked && (
        <text textAnchor="middle" y={r + (hasStar ? 33 : 17)}
          fontSize={isBoss ? 11 : 9.5} fontWeight="800"
          fontFamily="'Fredoka One','Nunito',sans-serif"
          fill={isBoss ? "#7f0000" : "#374151"} style={{ userSelect: "none" }}>
          {level.name}
        </text>
      )}

      {/* Locked tooltip with Unlock CTA */}
      {showTip && (
        <g onClick={e => e.stopPropagation()}>
          <rect x={-85} y={-r - 78} width={170} height={64} rx={10} fill="#1f2937" opacity={0.95}/>
          <polygon points="0,0 -8,-12 8,-12" fill="#1f2937" opacity={0.95} transform={`translate(0,${-r - 14})`}/>
          <text textAnchor="middle" y={-r - 60} fontSize={10} fontWeight="800"
            fontFamily="'Nunito',sans-serif" fill="#fff" style={{ userSelect: "none" }}>
            {level.name}
          </text>
          <text textAnchor="middle" y={-r - 46} fontSize={9}
            fontFamily="'Nunito',sans-serif" fill="#fca5a5" style={{ userSelect: "none" }}>
            {level.song}
          </text>
          {/* Unlock button inside tooltip */}
          <g onClick={onPaywall} style={{ cursor: "pointer" }}>
            <rect x={-38} y={-r - 40} width={76} height={20} rx={10} fill="#f59e0b"/>
            <text textAnchor="middle" y={-r - 26} fontSize={9} fontWeight="900"
              fontFamily="'Nunito',sans-serif" fill="#fff" style={{ userSelect: "none" }}>
              🔓 Unlock All →
            </text>
          </g>
        </g>
      )}
    </g>
  );
}

/* ── Lightweight SVG decorations ─────────────────────────────── */
function MapDecos() {
  return (
    <g>
      {/* Hills */}
      <path d="M0 840 Q60 800 120 818 Q180 838 240 808 Q300 778 360 808 L360 940 L0 940Z" fill="#a8df7a"/>
      <path d="M0 878 Q90 853 180 868 Q270 852 360 872 L360 940 L0 940Z" fill="#8ed45a"/>
      <path d="M0 910 Q90 900 180 908 Q270 900 360 910 L360 940 L0 940Z" fill="#7ec850"/>
      <ellipse cx={55}  cy={940} rx={110} ry={42} fill="#9fda72"/>
      <ellipse cx={308} cy={940} rx={118} ry={38} fill="#92d468"/>
      {/* Clouds */}
      <g opacity=".85"><ellipse cx={58}  cy={62}  rx={38} ry={15} fill="white"/><ellipse cx={44}  cy={52}  rx={24} ry={15} fill="white"/><ellipse cx={76}  cy={54}  rx={20} ry={12} fill="white"/></g>
      <g opacity=".78"><ellipse cx={268} cy={88}  rx={46} ry={18} fill="white"/><ellipse cx={250} cy={76}  rx={28} ry={18} fill="white"/><ellipse cx={290} cy={80}  rx={24} ry={14} fill="white"/></g>
      {/* Trees */}
      <g transform="translate(18,718)scale(.78)"><rect x="-3" y="8" width="7" height="18" fill="#8B5E3C" rx="2"/><ellipse cx="0" cy="-2" rx="14" ry="19" fill="#3d8c2f"/><ellipse cx="0" cy="-6" rx="10" ry="13" fill="#52aa40"/></g>
      <g transform="translate(338,625)scale(.76)"><rect x="-3" y="8" width="7" height="18" fill="#8B5E3C" rx="2"/><ellipse cx="0" cy="-2" rx="14" ry="19" fill="#4ea83a"/><ellipse cx="0" cy="-6" rx="10" ry="13" fill="#52aa40"/></g>
      <g transform="translate(20,310)scale(.7)"><rect x="-3" y="8" width="7" height="18" fill="#8B5E3C" rx="2"/><ellipse cx="0" cy="-2" rx="14" ry="19" fill="#3d8c2f"/><ellipse cx="0" cy="-6" rx="10" ry="13" fill="#52aa40"/></g>
      <g transform="translate(338,230)scale(.72)"><rect x="-3" y="8" width="7" height="18" fill="#8B5E3C" rx="2"/><ellipse cx="0" cy="-2" rx="14" ry="19" fill="#46a036"/><ellipse cx="0" cy="-6" rx="10" ry="13" fill="#52aa40"/></g>
      {/* Flowers */}
      <circle cx="148" cy="900" r="4" fill="#ff9eb5"/><circle cx="148" cy="900" r="2.5" fill="#ffe066"/>
      <circle cx="202" cy="893" r="4" fill="#a8e6cf"/><circle cx="202" cy="893" r="2.5" fill="#ffe066"/>
      <circle cx="256" cy="904" r="3.5" fill="#ffb3ba"/><circle cx="256" cy="904" r="2" fill="#ffe066"/>
      {/* Castle ghost */}
      <g opacity=".12" transform="translate(180,52)">
        <rect x="-30" y="-72" width="60" height="80" rx="3" fill="#333"/>
        <rect x="-34" y="-82" width="15" height="18" rx="2" fill="#333"/>
        <rect x="-8"  y="-85" width="15" height="21" rx="2" fill="#333"/>
        <rect x="18"  y="-82" width="15" height="18" rx="2" fill="#333"/>
      </g>
      {/* Distant mountains */}
      <path d="M0 225 Q55 158 115 196 Q165 132 218 182 Q268 112 322 170 Q348 150 360 177 L360 242 L0 242Z" fill="#c8eef8" opacity=".4"/>
    </g>
  );
}

/* ── Styles ───────────────────────────────────────────────────── */
const OVERLAY = {
  position: "fixed", inset: 0, zIndex: 9999,
  background: "rgba(0,0,0,0.58)",
  backdropFilter: "blur(8px)",
  WebkitBackdropFilter: "blur(8px)",
  display: "flex", alignItems: "center", justifyContent: "center",
  padding: "12px",
};
const SHEET = {
  background: "white", borderRadius: 28,
  width: "100%", maxWidth: 440,
  maxHeight: "92vh",
  display: "flex", flexDirection: "column",
  boxShadow: "0 32px 80px rgba(0,0,0,0.32)",
  overflow: "hidden",
  animation: "ttmm-pop .38s cubic-bezier(.34,1.56,.64,1)",
};
const HEADER = {
  display: "flex", alignItems: "center", justifyContent: "space-between",
  padding: "14px 16px 10px",
  borderBottom: "2px solid #d1fae5",
  flexShrink: 0,
};
const HEADER_TITLE = {
  fontFamily: "'Fredoka One','Nunito',sans-serif",
  fontSize: 20, color: "#166534", letterSpacing: ".4px",
};
const HEADER_SUB = {
  fontSize: 11, fontWeight: 800, color: "#6b7280",
  fontFamily: "'Nunito',sans-serif", marginTop: 1,
};
const CLOSE_BTN = {
  width: 34, height: 34, borderRadius: 10,
  background: "#f9fafb", border: "1.5px solid #e5e7eb",
  fontSize: 15, color: "#6b7280", cursor: "pointer",
  display: "flex", alignItems: "center", justifyContent: "center",
  fontWeight: 800, lineHeight: 1,
  transition: "background .15s",
};
const MAP_SCROLL = {
  flex: 1, overflowY: "auto", overflowX: "hidden",
  padding: "0 8px 4px",
};
const LEGEND = {
  display: "flex", flexWrap: "wrap",
  justifyContent: "center", gap: "4px 12px",
  padding: "10px 14px 14px",
  borderTop: "1.5px solid #d1fae5",
  flexShrink: 0,
};
const LEG_ITEM = {
  display: "inline-flex", alignItems: "center", gap: 5,
  fontSize: 11, fontWeight: 800, color: "#374151",
  fontFamily: "'Nunito',sans-serif",
};
const LEG_DOT = {
  width: 10, height: 10, borderRadius: "50%",
  display: "inline-block", flexShrink: 0,
};

const MODAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Fredoka+One&family=Nunito:wght@700;800;900&display=swap');
  @keyframes ttmm-pop {
    from { transform: scale(0.88) translateY(24px); opacity: 0; }
    to   { transform: scale(1)    translateY(0);    opacity: 1; }
  }
  @keyframes ttmm-cur-pulse {
    0%,100% { transform: scale(1);    opacity: .75; }
    50%     { transform: scale(1.38); opacity: .15; }
  }
  @keyframes ttmm-boss-pulse {
    0%,100% { transform: scale(1);    opacity: .45; }
    50%     { transform: scale(1.28); opacity: .1;  }
  }
  .ttmm-cur-pulse  { animation: ttmm-cur-pulse  1.7s ease-in-out infinite; transform-origin:center; transform-box:fill-box; }
  .ttmm-boss-pulse { animation: ttmm-boss-pulse 2.3s ease-in-out infinite; transform-origin:center; transform-box:fill-box; }
`;